import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Layers, UserCog, Users, type LucideIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';

/** The five figures the screen opens with. */
export interface ClassesTotals {
  classes: number;
  sections: number;
  students: number;
  teachers: number;
  subjects: number;
}

/** Which panel of the academic structure a figure is answered on. */
export type StructureFocus = 'bands' | 'subjects';

interface TileConfig {
  key: keyof ClassesTotals;
  label: string;
  hint: string;
  icon: LucideIcon;
  cardStyle: string;
  dotStyle: string;
  labelStyle: string;
  opens:
    | { kind: 'filter' }
    | { kind: 'structure'; focus: StructureFocus }
    | { kind: 'to'; path: string };
  action: string;
}

const TILES: TileConfig[] = [
  {
    key: 'classes',
    label: 'Total classes',
    hint: 'Across all grades',
    icon: GraduationCap,
    cardStyle: 'bg-[#fffbeb] dark:bg-slate-900 border-[#fde68a] dark:border-slate-800 shadow-lg shadow-amber-500/15 hover:shadow-xl hover:shadow-amber-500/25 dark:shadow-none dark:hover:shadow-none',
    dotStyle: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706] shadow-md shadow-amber-500/35 dark:shadow-none text-white',
    labelStyle: 'text-amber-950 dark:text-white',
    opens: { kind: 'filter' },
    action: 'Show every class on the roster',
  },
  {
    key: 'sections',
    label: 'Total sections',
    hint: 'Across all classes',
    icon: Layers,
    cardStyle: 'bg-[#f0fdfa] dark:bg-slate-900 border-[#99f6e4] dark:border-slate-800 shadow-lg shadow-teal-500/15 hover:shadow-xl hover:shadow-teal-500/25 dark:shadow-none dark:hover:shadow-none',
    dotStyle: 'bg-gradient-to-br from-[#0d9488] to-[#0f766e] shadow-md shadow-teal-500/35 dark:shadow-none text-white',
    labelStyle: 'text-teal-950 dark:text-white',
    opens: { kind: 'structure', focus: 'bands' },
    action: 'See the sections in each grade band',
  },
  {
    key: 'students',
    label: 'Total students',
    hint: 'Enrolled this academic year',
    icon: Users,
    cardStyle: 'bg-[#eaf8f0] dark:bg-slate-900 border-[#c3eed4] dark:border-slate-800 shadow-lg shadow-emerald-500/15 hover:shadow-xl hover:shadow-emerald-500/25 dark:shadow-none dark:hover:shadow-none',
    dotStyle: 'bg-gradient-to-br from-[#10b981] to-[#059669] shadow-md shadow-emerald-500/35 dark:shadow-none text-white',
    labelStyle: 'text-emerald-900 dark:text-white',
    opens: { kind: 'to', path: '/admin/students' },
    action: 'Open the student register',
  },
  {
    key: 'teachers',
    // Read off each section's class teacher — subject teachers are not
    // counted, so the tile says which teachers these are.
    label: 'Class teachers',
    hint: 'In charge of a section',
    icon: UserCog,
    cardStyle: 'bg-[#fff3eb] dark:bg-slate-900 border-[#fed7aa] dark:border-slate-800 shadow-lg shadow-orange-500/15 hover:shadow-xl hover:shadow-orange-500/25 dark:shadow-none dark:hover:shadow-none',
    dotStyle: 'bg-gradient-to-br from-[#f97316] to-[#ea580c] text-white shadow-md shadow-orange-500/35 dark:shadow-none',
    labelStyle: 'text-orange-950 dark:text-white',
    opens: { kind: 'to', path: '/admin/staff' },
    action: 'Open the staff list',
  },
  {
    key: 'subjects',
    // Subjects some class teaches, not the school's whole subject list — the
    // Subjects page counts that one.
    label: 'Subjects in use',
    hint: 'Taught in at least one class',
    icon: BookOpen,
    cardStyle: 'bg-[#fbf0ff] dark:bg-slate-900 border-[#f0c8fe] dark:border-slate-800 shadow-lg shadow-purple-500/15 hover:shadow-xl hover:shadow-purple-500/25 dark:shadow-none dark:hover:shadow-none',
    dotStyle: 'bg-gradient-to-br from-[#a855f7] to-[#9333ea] shadow-md shadow-purple-500/35 dark:shadow-none text-white',
    labelStyle: 'text-purple-900 dark:text-white',
    opens: { kind: 'structure', focus: 'subjects' },
    action: 'See which classes teach each subject',
  },
];

export interface ClassesStatRowProps {
  totals: ClassesTotals;
  isLoading?: boolean;
  /** True when nothing is narrowing the roster, so the first tile reads as on. */
  isShowingAll: boolean;
  onShowAllClasses: () => void;
  onOpenStructure: (focus: StructureFocus) => void;
}

export function ClassesStatRow({
  totals,
  isLoading = false,
  isShowingAll: _isShowingAll,
  onShowAllClasses,
  onOpenStructure,
}: ClassesStatRowProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {TILES.map(({ key, label, hint, icon: Icon, cardStyle, dotStyle, labelStyle, opens, action }) => {
        const content = (
          <>
            <div className="flex items-start justify-between gap-2.5">
              <div className="min-w-0 flex-1">
                <span className={cn('text-xs sm:text-[14px] font-bold block leading-tight', labelStyle)}>
                  {label}
                </span>
                {isLoading ? (
                  <Skeleton className="h-7 w-16 mt-1 rounded-md" />
                ) : (
                  <span className="font-black text-slate-800 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
                    {totals[key].toLocaleString('en-IN')}
                  </span>
                )}
              </div>
              <div
                className={cn(
                  'h-8 w-8 sm:h-9 sm:w-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105',
                  dotStyle,
                )}
              >
                <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5" />
              </div>
            </div>
            <span className="text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 block mt-1.5 truncate">
              {hint}
            </span>
          </>
        );

        const commonClass = cn(
          'group rounded-2xl border p-3.5 flex flex-col justify-between shadow-lg dark:shadow-none hover:shadow-2xl dark:hover:shadow-none hover:-translate-y-1 transition-all duration-200 text-left cursor-pointer',
          cardStyle,
        );

        if (opens.kind === 'to') {
          return (
            <Link key={key} to={opens.path} aria-label={action} className={commonClass}>
              {content}
            </Link>
          );
        }

        return (
          <button
            key={key}
            type="button"
            aria-label={action}
            onClick={
              opens.kind === 'filter'
                ? onShowAllClasses
                : opens.kind === 'structure'
                  ? () => onOpenStructure(opens.focus)
                  : undefined
            }
            className={commonClass}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}
